import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Form, Typography } from 'antd';
import Table, { ColumnType } from 'antd/lib/table';
import { concat, keyBy, partition, sum, transform } from 'lodash';
import { useTranslation } from 'react-i18next';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useVT } from 'virtualizedtableforantd4';

import { EditableCell } from 'common-ui/cells';
import { EditableListing } from 'common-ui/cells/types';
import { mergeColumnSettings } from 'common-ui/listingConfig';
import { EmptyWrapper, PopConfirmCancelWrapper } from 'common-ui/wrappers';
import {
  contactBookApi,
  ContactBookListing,
  ContactBookListingUpdate,
  ListingConfig,
  listingConfigApi,
} from 'core/lib';
import { OBJECT_TYPES } from 'core/lib/constants';

import { columns as rawColumns } from './columns';
import { SharedCasesRenderer } from '../components';
import ListingFilters from '../filters/filters';

const { Link } = Typography;

const ACTIONS_WIDTH = 150;

type ContactBookListingComponentProps = {
  listingData?: ContactBookListing[];
};

const ContactBookListingComponent = ({ listingData }: ContactBookListingComponentProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [formDirty, setFormDirty] = useState(false);

  const [updateListingItem, { isLoading: isSaving, isSuccess: isSavingSuccess }] =
    contactBookApi.useUpdateListingItemMutation();

  const isEditing = useCallback((record: ContactBookListing) => record.id === editingKey, [editingKey]);

  const handleValuesChanged = () => {
    setFormDirty(true);
  };

  const edit = (record: ContactBookListingUpdate) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id!);
  };

  const cancel = () => {
    form.resetFields();
    setEditingKey(null);
    setFormDirty(false);
  };

  useEffect(() => {
    if (isSavingSuccess) {
      setEditingKey(null);
    }
  }, [isSavingSuccess]);

  const { data: listingConfig } = listingConfigApi.useGetListingConfigQuery(OBJECT_TYPES.CONTACT_BOOK);

  const columns = useMemo(() => {
    const orderedColumns = mergeColumnSettings(rawColumns, keyBy(listingConfig, 'dataIndex')).filter(
      (col) => col.visible
    );

    return concat(...partition(orderedColumns, { pinned: true }));
  }, [listingConfig]);

  const save = async (key: React.Key) => {
    const row: ContactBookListingUpdate = transform(await form.validateFields(), (result, value, key) => {
      result[key] = value ?? null;
    });
    row.id = editingKey!;

    updateListingItem(row);
  };

  const mergedColumns = useMemo(
    () =>
      [
        ...columns.map((col) => {
          if (!col.editable || !col.inputType) {
            return col;
          }

          return {
            ...col,
            fixed: col.pinned && 'left',
            onCell: (record: ContactBookListing) => {
              const inputProps = col.getInputProps ? col.getInputProps(record) : col.inputProps;
              return {
                record,
                inputType: col.inputType,
                inputProps,
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
                isSaving: isSaving,
                rules: col.inputRules,
              };
            },
          };
        }),
        {
          title: t('global:general.actions'),
          dataIndex: 'actions',
          width: ACTIONS_WIDTH,
          render: (_: boolean, record: ContactBookListing) => {
            const editable = isEditing(record);
            return editable ? (
              <div className="flex items-center gap-2">
                <Button
                  type="link"
                  disabled={isSaving || !formDirty}
                  loading={isSaving}
                  onClick={() => save(record.id)}
                >
                  {t('actions:global.save')}
                </Button>
                {!isSaving && (
                  <PopConfirmCancelWrapper
                    title={t('actions:confirmation.confirmCancel')}
                    onConfirm={cancel}
                    disabled={!formDirty}
                  >
                    <Link>{t('actions:global.cancel')}</Link>
                  </PopConfirmCancelWrapper>
                )}
              </div>
            ) : (
              <Link disabled={editingKey !== null} onClick={() => edit(record)}>
                {t('actions:global.edit')}
              </Link>
            );
          },
        } as ColumnType<ContactBookListing>,
      ] as (ColumnType<ContactBookListing> & ListingConfig & EditableListing<ContactBookListing>)[],
    [columns, isSaving, isEditing, editingKey, t, formDirty] // eslint-disable-line  react-hooks/exhaustive-deps
  );

  const columnsWidth = useMemo(() => sum(columns.map(({ width }) => width).concat(ACTIONS_WIDTH)), [columns]);

  const [vt, set_components] = useVT(() => ({ scroll: { x: columnsWidth, y: 500 } }), []);

  useEffect(() => {
    set_components({ body: { cell: EditableCell } });
  }, [set_components]);

  return (
    <>
      <ListingFilters />
      <EmptyWrapper description={t('global:results.emptySearch')} empty={!listingData?.length}>
        <AutoSizer disableWidth className="h-full">
          {({ height }) => (
            <Form
              onValuesChange={handleValuesChanged}
              form={form}
              component={false}
              layout="vertical"
              name="contactBookForm"
            >
              {vt.body?.cell && (
                <Table<ContactBookListing>
                  className="h-full ant-table-title-padding"
                  rowClassName="ant-design-table-row"
                  rowKey="id"
                  columns={mergedColumns}
                  dataSource={listingData}
                  pagination={false}
                  size="middle"
                  scroll={{ y: height - 100, scrollToFirstRowOnChange: true }}
                  components={{ ...vt }}
                  expandable={{
                    expandedRowRender: SharedCasesRenderer,
                    rowExpandable: (record) => !!record.sharedCases?.length,
                  }}
                />
              )}
            </Form>
          )}
        </AutoSizer>
      </EmptyWrapper>
    </>
  );
};

export default ContactBookListingComponent;
